import logger from '@eventbrite/client-logger';
import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { formatMajorMoney } from '@eventbrite/intl';
import { trackIntercomEvent } from '@eventbrite/marketing-tools-common';
import { PricingAndPackagingAPIError } from '../error';
import { fetchOrganizationPlan, PlanTier } from '../plans/index';
import { getPlanNameFromPlanTier } from '../plans/utils';
import { PricingAndPackagingFeatureFlags } from '../types';
import {
    getPlaceFromRequest,
    getPlanPricingObject,
    getTimestampInSeconds,
    getUserLocaleAndCurrency,
    sendUpdateToIntercom,
} from '../utils';
import {
    CancelSubscriptionParams,
    MigrateSubscriptionParams,
    MigrateSubscriptionPayload,
    PostSubscriptionPayload,
    SubscriptionObject,
    SubscriptionParams,
} from './types';
import {
    extractErrorMessage,
    validateMigrateSubscriptionPayload,
    validateSubscriptionPayload,
} from './utils';

export const fetchOrganizationSubscription = async (
    organizationId: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
) => {
    if (!organizationId) {
        throw new Error(
            '[fetchOrganizationSubscription]: organizationId is a required parameter.',
        );
    }

    let response;

    try {
        response = await sdkRequest(
            `/organizations/${organizationId}/subscription/`,
        );

        const intercomUpdateObject = {} as any;

        if (response?.status === 'active' || response?.status === 'past_due') {
            if (response.cancel_at_period_end) {
                intercomUpdateObject.pp_subscription_expiration_date_at =
                    getTimestampInSeconds(response.current_period_end);
                intercomUpdateObject.pp_subscription_next_charge_at = null;
                intercomUpdateObject.pp_subscription_next_charge_amount = null;
            } else {
                intercomUpdateObject.pp_subscription_expiration_date_at = null;
                intercomUpdateObject.pp_subscription_canceled_at = null;
                intercomUpdateObject.pp_subscription_next_charge_at =
                    getTimestampInSeconds(response.current_period_end);
            }

            intercomUpdateObject.pp_trial_type = null;
            intercomUpdateObject.pp_trial_status = null;

            if (response.plan_tier) {
                const planName = getPlanNameFromPlanTier(
                    response.plan_tier as PlanTier,
                    { featureFlags: options?.featureFlags },
                );

                intercomUpdateObject.pp_subscription_pro_plan_name = planName;
                intercomUpdateObject.is_free_admin =
                    response.plan_tier.includes('ADMIN');
                intercomUpdateObject.is_npo =
                    response.plan_tier.includes('NPO');
            }
        } else {
            intercomUpdateObject.pp_subscription_pro_plan_name = null;
            intercomUpdateObject.is_free_admin = false;
            intercomUpdateObject.is_npo = false;
        }

        if (
            response &&
            (!response.future_phases || !response.future_phases.length)
        ) {
            intercomUpdateObject.pp_dg_subscription_pro_plan_name = null;
            intercomUpdateObject.pp_dg_subscription_next_charge_amount = null;
            intercomUpdateObject.pp_dg_ticket_limit = null;
            intercomUpdateObject.pp_dg_email_daily_send_limit = null;
        }

        sendUpdateToIntercom(intercomUpdateObject);
    } catch (error) {
        logger.error(error, {
            message: `Error in fetchOrganizationSubscription for organization ${organizationId}`,
        });

        throw new PricingAndPackagingAPIError({
            message:
                'There was an error fetching your subscription. Please try again or contact support for assistance.',
            statusCode: error.response.status,
            parsedError: error.parsedError,
        });
    }

    return response as SubscriptionObject;
};

const sendSubscriptionInfoToIntercom = async (
    organizationId: string,
    intercomUpdateObject: any,
    currency: string,
    email: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
) => {
    try {
        const organizationPlan = await fetchOrganizationPlan(
            organizationId,
            options,
        );

        const billingPeriodInterval =
            organizationPlan?.pricing?.[0]?.period?.interval;

        const planPricing = getPlanPricingObject(
            organizationPlan,
            currency,
            billingPeriodInterval,
        );

        intercomUpdateObject.pp_currency = currency.toUpperCase();
        intercomUpdateObject.pp_subscription_next_charge_amount =
            formatMajorMoney(
                planPricing?.currency_options[currency.toLowerCase()].value,
                currency.toUpperCase(),
            );
        intercomUpdateObject.pp_current_ticket_limit = isNaN(
            //eslint-disable-next-line
            //@ts-ignore
            organizationPlan.features.event_capacity,
        )
            ? gettext('unlimited')
            : `${organizationPlan.features.event_capacity}`;
        intercomUpdateObject.pp_email_daily_send_limit =
            organizationPlan.features.campaign_daily_limit;

        await sendUpdateToIntercom(intercomUpdateObject);

        if (email) {
            trackIntercomEvent('PP pro subscription started', {
                email: email,
            });
        }
    } catch (error) {
        logger.error(error, {
            message: 'Error sending new subscription info to Intercom:',
        });
    }
};

// For legal reasons we need remind Colorado yearly subscribers about renewals
const isCreatorInColorado = async () => {
    let inColorado = false;

    try {
        const locationData = await getPlaceFromRequest();

        if (
            locationData?.place?.place_id === '85688603' ||
            locationData?.place?.parent_id === '85688603'
        ) {
            inColorado = true;
        }
    } catch (error) {
        logger.error(error, {
            message: 'Error determining if creator is in Colorado based on IP',
        });
    }

    return inColorado;
};

export const startOrganizationSubscription = async ({
    organizationId,
    payload,
    options,
}: SubscriptionParams): Promise<SubscriptionObject | undefined> => {
    if (!organizationId) {
        throw new Error(
            '[startOrganizationSubscription]: organizationId is a required parameter.',
        );
    }

    validateSubscriptionPayload(payload as PostSubscriptionPayload);

    let response;

    try {
        response = await sdkRequest(
            `/organizations/${organizationId}/subscription/`,
            { method: 'POST', body: JSON.stringify(payload) },
        );

        if (response) {
            const isInColorado = await isCreatorInColorado();

            const nextChargeTimestamp = getTimestampInSeconds(
                response.current_period_end,
            );

            // If next charge is 35+ days from now, safe to assume it is a yearly plan
            // (side note: all of this Intercom stuff is digusting btw, please don't assume
            // I like any of the monstrosity I've created out of necessity here)
            const currentDate = new Date();

            currentDate.setDate(currentDate.getDate() + 35);

            // Get the timestamp in seconds
            const thirtyFiveDaysTimestamp = Math.floor(
                currentDate.getTime() / 1000,
            );

            let subscriptionInterval = 'month';

            if (
                nextChargeTimestamp &&
                nextChargeTimestamp > thirtyFiveDaysTimestamp
            ) {
                subscriptionInterval = 'year';
            }
            const intercomUpdateObject: any = {
                pp_is_creator_in_colorado: isInColorado,
                pp_subscription_interval: subscriptionInterval,
                pp_subscription_next_charge_at: nextChargeTimestamp,
                pp_subscription_started_at: getTimestampInSeconds(
                    response.created_at,
                ),
                pp_subscription_expiration_date_at: null,
                pp_subscription_canceled_at: null,
                pp_trial_status: null,
            };

            if (response.plan_tier) {
                const planName = getPlanNameFromPlanTier(
                    response.plan_tier as PlanTier,
                    { featureFlags: options?.featureFlags },
                );

                intercomUpdateObject.pp_subscription_pro_plan_name = planName;
                intercomUpdateObject.is_free_admin =
                    response.plan_tier.includes('ADMIN');
                intercomUpdateObject.is_npo =
                    response.plan_tier.includes('NPO');
            }

            const userEmail = window?.__SERVER_DATA__?.user?.email;

            sendSubscriptionInfoToIntercom(
                organizationId,
                intercomUpdateObject,
                response.currency,
                userEmail,
                options,
            );
        }
    } catch (error) {
        logger.error(error, {
            message: 'Error in startOrganizationSubscription:',
        });

        let errorMessage = `There was an issue starting your subscription. Please try again or contact support for assistance.`;

        if (error.response.status == 402) {
            errorMessage =
                'There was an issue processing your payment. Please update your payment method and try again.';
        }

        throw new PricingAndPackagingAPIError({
            message: errorMessage,
            statusCode: error.response.status,
            parsedError: {
                error: {
                    ...error.parsedError,
                    message: extractErrorMessage(
                        error.parsedError?.error?.message,
                    ),
                },
            },
        });
    }

    return response;
};

export const updateOrganizationSubscription = async ({
    organizationId,
    payload,
    options,
}: SubscriptionParams): Promise<SubscriptionObject | undefined> => {
    if (!organizationId) {
        throw new Error(
            '[updateOrganizationSubscription]: organizationId is a required parameter.',
        );
    }

    if (!payload.plan_id || !payload.plan_price_id) {
        throw new Error(
            '[updateOrganizationSubscription]: plan_id and plan_price_id are required parameters.',
        );
    }

    let response;

    try {
        response = await sdkRequest(
            `/organizations/${organizationId}/subscription/`,
            { method: 'PUT', body: JSON.stringify(payload) },
        );

        const eventbriteUser = window?.__SERVER_DATA__?.user;

        if (response) {
            const isInColorado = await isCreatorInColorado();

            const intercomUpdateObject: any = {
                pp_is_creator_in_colorado: isInColorado,
                pp_subscription_next_charge_at: getTimestampInSeconds(
                    response.current_period_end,
                ),
                pp_subscription_started_at: getTimestampInSeconds(
                    response.created_at,
                ),
            };

            let isDowngrade = false;

            if (response.future_phases && response.future_phases.length) {
                isDowngrade = true;

                const downgradedPlan = response.future_phases[0];

                const { currency } = getUserLocaleAndCurrency();

                // currency has been added in response - this is a fallback that can be removed soon
                const subscriptionCurrency = response.currency || currency;

                intercomUpdateObject.pp_dg_subscription_pro_plan_name =
                    getPlanNameFromPlanTier(
                        downgradedPlan.plan_tier as PlanTier,
                        { featureFlags: options?.featureFlags },
                    );

                intercomUpdateObject.pp_dg_subscription_next_charge_amount =
                    formatMajorMoney(
                        downgradedPlan.pricing.currency_options[
                            subscriptionCurrency.toLowerCase()
                        ].value,
                        subscriptionCurrency.toUpperCase(),
                    );
                intercomUpdateObject.pp_dg_ticket_limit = isNaN(
                    //eslint-disable-next-line
                    //@ts-ignore
                    downgradedPlan.features.event_capacity,
                )
                    ? gettext('unlimited')
                    : `${downgradedPlan.features.event_capacity}`;
                intercomUpdateObject.pp_dg_email_daily_send_limit =
                    downgradedPlan.features.campaign_daily_limit;
            } else if (
                !response.future_phases ||
                !response.future_phases.length
            ) {
                intercomUpdateObject.pp_dg_subscription_pro_plan_name = null;
                intercomUpdateObject.pp_dg_subscription_next_charge_amount =
                    null;
                intercomUpdateObject.pp_dg_ticket_limit = null;
                intercomUpdateObject.pp_dg_email_daily_send_limit = null;
            }

            sendUpdateToIntercom(intercomUpdateObject);

            if (eventbriteUser) {
                trackIntercomEvent('PP pro subscription started', {
                    email: eventbriteUser.email,
                });
            }

            if (eventbriteUser) {
                trackIntercomEvent(
                    `PP pro subscription ${
                        isDowngrade ? 'downgraded' : 'upgraded'
                    }`,
                    {
                        email: eventbriteUser.email,
                    },
                );
            }
        }
    } catch (error) {
        logger.error(error, {
            message: `Error in updateOrganizationSubscription for organization ${organizationId}`,
        });

        let errorMessage = `There was an issue updating your subscription. Please try again or contact support for assistance.`;

        if (error.response.status == 402) {
            errorMessage =
                'There was an issue processing your payment. Please update your payment method and try again.';
        }

        throw new PricingAndPackagingAPIError({
            message: errorMessage,
            statusCode: error.response.status,
            parsedError: {
                error: {
                    ...error.parsedError,
                    message: extractErrorMessage(
                        error.parsedError?.error?.message,
                    ),
                },
            },
        });
    }

    return response;
};

export const cancelOrganizationSubscription = async ({
    organizationId,
    actor,
    loggedInUserId,
    reasonCode,
}: CancelSubscriptionParams) => {
    if (!organizationId) {
        throw new Error(
            '[cancelOrganizationSubscription]: organizationId is a required parameter.',
        );
    }

    if (!['user', 'admin'].includes(actor)) {
        throw new Error(
            '[cancelOrganizationSubscription]: actor must be either be user or admin.',
        );
    }

    const params = {
        created_by: actor,
        logged_in_user_id: loggedInUserId,
        reason_code: reasonCode || 'user_requested',
    };

    let response;

    try {
        response = await sdkRequest(
            `/organizations/${organizationId}/subscription/`,
            {
                method: 'DELETE',
                body: JSON.stringify(params),
            },
        );

        // DELETE endpoint does not return the updated subscription record in response
        const intercomUpdateObject = {
            pp_subscription_next_charge_at: null,
            pp_subscription_canceled_at: getTimestampInSeconds('now'),
        };

        sendUpdateToIntercom(intercomUpdateObject);

        const eventbriteUser = window?.__SERVER_DATA__?.user;

        if (eventbriteUser) {
            trackIntercomEvent('PP pro subscription canceled', {
                email: eventbriteUser.email,
            });
        }
    } catch (error) {
        logger.error(error, {
            message: `Error in cancelOrganizationSubscription for organization ${organizationId}`,
        });

        throw new PricingAndPackagingAPIError({
            message: `There was an issue canceling your subscription. Please try again or contact support for assistance.`,
            statusCode: error.response.status,
            parsedError: error.parsedError,
        });
    }

    return response;
};

// Payload can be empty if cancelling, otherwise requires plan_id, plan_price_id, currency
export const migrateOrganizationSubscription = async ({
    organizationId,
    payload,
    isCancellation,
}: MigrateSubscriptionParams) => {
    if (!organizationId) {
        throw new Error(
            '[migrateOrganizationSubscription]: organizationId is a required parameter.',
        );
    }

    if (!isCancellation) {
        validateMigrateSubscriptionPayload(
            payload as MigrateSubscriptionPayload,
        );
    }

    let response;

    try {
        response = await sdkRequest(
            `/organizations/${organizationId}/subscription/migrate/${
                isCancellation ? '?is_cancellation=true' : ''
            }`,
            {
                method: 'POST',
                body: JSON.stringify(payload || {}),
            },
        );

        const getProPlanName = (planId: string | undefined) => {
            if (!planId) {
                return null;
            } else if (planId.startsWith('PLAN#LITE')) {
                return 'Pro 100';
            } else if (planId.startsWith('PLAN#CORE')) {
                return 'Pro 250';
            } else if (planId.startsWith('PLAN#PRO')) {
                return 'Pro Unlimited';
            } else {
                return null;
            }
        };

        const intercomUpdateObject = {
            is_migrated_pro_from_boost: true,
            is_cancelling_post_migration: !!isCancellation,
            migration_pro_plan_selected: !isCancellation
                ? getProPlanName(payload.plan_id)
                : null,
        };

        sendUpdateToIntercom(intercomUpdateObject);
    } catch (error) {
        logger.error(error, {
            message:
                'Error in migrateOrganizationSubscription for organization ${organizationId}',
        });

        throw new PricingAndPackagingAPIError({
            message: `There was an issue updating your subscription. Please try again or contact support for assistance.`,
            statusCode: error.response.status,
            parsedError: error.parsedError,
        });
    }

    return response;
};

export * from './types';
export * from './__mocks__';
